import type { ApiClient } from './client';

/** Mirrors @bilt/feature-flags limited domain (CRA cannot transpile package TS). */
const LIMITED_FLAG_KEYS = [
  'limited_designations_enabled',
  'founding_vehicle_designation_enabled',
] as const;

export type LimitedClientFlagKey = (typeof LIMITED_FLAG_KEYS)[number];
export type LimitedClientFlagMap = Partial<Record<LimitedClientFlagKey, boolean>>;

export type ClientConfigFlagsStructured = {
  paid?: Record<string, boolean>;
  passport?: Record<string, boolean>;
  email?: Record<string, boolean>;
  limited?: LimitedClientFlagMap;
  ai?: {
    enabled?: boolean;
    build_editing_enabled?: boolean;
  };
};

export interface ClientConfigPayload {
  registration?: {
    mode?: 'invite_only' | 'public';
    invite_required?: boolean;
    email_verification_required?: boolean;
  };
  environment?: string;
  feature_flags?: Record<string, boolean>;
  flags?: ClientConfigFlagsStructured;
  legacy_architecture_flags?: Record<string, boolean>;
  maintenance?: boolean;
  min_app_version?: string;
  recommended_app_version?: string;
}

export function isPublicRegistrationEnabled(data: ClientConfigPayload | null | undefined): boolean {
  return data?.registration?.mode === 'public' && data.registration.invite_required === false
    && data.registration.email_verification_required === true;
}

export function parseLimitedFlagsFromClientConfig(
  data: ClientConfigPayload | null | undefined
): LimitedClientFlagMap {
  const structured = data?.flags?.limited;
  if (structured && typeof structured === 'object') {
    return {
      limited_designations_enabled: Boolean(structured.limited_designations_enabled),
      founding_vehicle_designation_enabled: Boolean(
        structured.founding_vehicle_designation_enabled
      ),
    };
  }
  const flat = data?.feature_flags || {};
  return {
    limited_designations_enabled: Boolean(flat['limited.limited_designations_enabled']),
    founding_vehicle_designation_enabled: Boolean(
      flat['limited.founding_vehicle_designation_enabled']
    ),
  };
}

export function areLimitedDesignationsPubliclyEnabled(
  data: ClientConfigPayload | null | undefined
): boolean {
  const flags = parseLimitedFlagsFromClientConfig(data);
  return Boolean(flags.limited_designations_enabled && flags.founding_vehicle_designation_enabled);
}

export function isAiBuildEditingEnabled(
  data: ClientConfigPayload | null | undefined
): boolean {
  if (data?.flags?.ai && typeof data.flags.ai.build_editing_enabled === 'boolean') {
    return data.flags.ai.build_editing_enabled;
  }
  return Boolean(data?.feature_flags?.['ai.build_editing_enabled']);
}

export const clientConfigMethods = {
  async getClientConfig(): Promise<ClientConfigPayload> {
    const response = await this.makeRequest<ClientConfigPayload>('/client_config.php', {
      method: 'GET',
    });
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to load client config');
    }
    return response.data || {};
  },

  async getLimitedDesignationClientFlags(): Promise<LimitedClientFlagMap> {
    const response = await this.makeRequest<ClientConfigPayload>('/client_config.php', {
      method: 'GET',
    });
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to load client config');
    }
    return parseLimitedFlagsFromClientConfig(response.data || {});
  },

  async getMapsConfig(): Promise<{ places_enabled: boolean; maps_api_key: string | null }> {
    const response = await this.makeRequest<{
      places_enabled?: boolean;
      maps_api_key?: string | null;
    }>('/maps_config.php', { method: 'GET' });
    if (!response.success) {
      return { places_enabled: false, maps_api_key: null };
    }
    return {
      places_enabled: Boolean(response.data?.places_enabled && response.data?.maps_api_key),
      maps_api_key: response.data?.maps_api_key ?? null,
    };
  },
} satisfies ThisType<ApiClient>;
